import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useCompany } from '../context/CompanyContext';
import { ALL_SERVICE_IDS } from '../constants/serviceCatalog';

/**
 * Subscribe to /companies/{companyId}/settings/activeServices and return the
 * set of service IDs that should be visible in the Calculator.
 *
 * Default-ON semantics:
 *   - missing doc             → all services visible
 *   - missing key for a svc   → that service is visible
 *   - explicit `false`        → hidden
 *
 * Returns the superset as `activeServices[]`. Calculator's ServiceSelector
 * filters its SERVICE_CATALOG by this list.
 */
export function useActiveServices() {
  const { companyId } = useCompany();
  const [servicesMap, setServicesMap] = useState(null); // null = not loaded yet
  const [error, setError]             = useState(null);

  useEffect(() => {
    if (!companyId) {
      setServicesMap(null);
      return undefined;
    }
    const ref = doc(db, 'companies', companyId, 'settings', 'activeServices');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setError(null);
        setServicesMap(snap.exists() ? (snap.data()?.services || {}) : {});
      },
      (err) => {
        console.warn('[useActiveServices] snapshot error:', err?.message);
        setError(err);
        setServicesMap({}); // fail-open: treat as "all on"
      },
    );
    return () => unsub();
  }, [companyId]);

  const loading = servicesMap === null && !error;

  // Default-ON: a service is active unless its value is explicitly false.
  const activeServices = ALL_SERVICE_IDS.filter(
    (id) => (servicesMap ?? {})[id] !== false
  );

  return { activeServices, loading, error };
}
